import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import "../styles/Capabilities.css";

const QUERY = `*[_type == "capabilitiesSection"][0]{
  heading, highlightedText, featureCards
}`;

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.8, 0.25, 1],
    },
  },
};

const imageVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      delay: 0.2,
    },
  },
};


const ExpandableDescription = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 250; // Character limit for truncation

  if (!text) return null;

  // Replace literal \n sequences from Sanity with actual newlines
  const cleanText = text.replace(/\\n/g, '\n');
  const shouldTruncate = cleanText.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? cleanText : `${cleanText.substring(0, maxLength).trim()}...`;

  return (
    <div className="feature-description-wrapper">
      <p className="feature-description" style={{ whiteSpace: "pre-line" }}>
        {displayText}
        {shouldTruncate && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            style={{
              color: "#000",
              cursor: "pointer",
              fontWeight: "600",
              marginLeft: "6px",
              textDecoration: "underline",
              fontSize: "14px"
            }}
          >
            {isExpanded ? "Show Less" : "Read More"}
          </span>
        )}
      </p>
    </div>
  );
};

const FeatureSection = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    sanityClient.fetch(QUERY).then(res => {
      setData(res);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };
    handleResize(); // initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cards = data?.featureCards || [];

  const getImage = (img, imgMobile) => {
    const dUrl = img ? urlFor(img).url() : "";
    const mUrl = imgMobile ? urlFor(imgMobile).url() : dUrl;
    return isDesktop ? dUrl : mUrl;
  };

  return (
    <>
      {!isLoading && (
        <section className="feature-section" id="features">
          {isDesktop ? (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={containerVariants}
            >
              <motion.h2
                className="feature-heading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {data.heading}{" "}
                <span className="highlighted-text">{data.highlightedText}</span>
              </motion.h2>

              <div className="feature-grid-complex">
                {cards.map((card, index) => (
                  <motion.div
                    className={`feature-card ${card.fullSpan ? "full-span horizontal-card" : ""}`}
                    key={index}
                    variants={cardVariants}
                    whileHover={{
                      scale: 1.03,
                      boxShadow: "0 12px 24px rgb(180, 180, 180) ",
                    }}
                  >
                    {card.fullSpan ? (
                      <div className="horizontal-content">
                        <motion.img
                          src={getImage(card.image, card.imageMobile)}
                          className="horizontal-image"
                          alt="Chart"
                          variants={imageVariants}
                        />
                        <div className="horizontal-text">
                          <h3 className="feature-title">{card.title}</h3>
                          <ExpandableDescription text={card.description} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="feature-text">
                          <h3 className="feature-title">{card.title}</h3>
                          <ExpandableDescription text={card.description} />
                        </div>
                        <motion.div className="feature-image-wrapper" variants={imageVariants}>
                          <img
                            src={getImage(card.image, card.imageMobile)}
                            alt="AI Card"
                            className="floating-ai-card"
                          />
                        </motion.div>
                      </>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            <div>
              <h2 className="feature-heading">
                Everything you need to build{" "}
                <span className="highlighted-text">the agents of the Future</span>
              </h2>

              <div className="feature-grid-complex">
                {cards.map((card, index) => (
                  <div
                    className={`feature-card ${card.fullSpan ? "full-span horizontal-card" : ""}`}
                    key={index}
                  >
                    {card.fullSpan ? (
                      <div className="horizontal-content">
                        <img
                          src={getImage(card.img, card.imgMobile)}
                          className="horizontal-image"
                          alt="Chart"
                        />
                        <div className="horizontal-text">
                          <h3 className="feature-title">{card.title}</h3>
                          <ExpandableDescription text={card.desc} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="feature-text">
                          <h3 className="feature-title">{card.title}</h3>
                          <ExpandableDescription text={card.desc} />
                        </div>
                        <div className="feature-image-wrapper">
                          <img
                            src={getImage(card.img, card.imgMobile)}
                            alt="AI Card"
                            className="floating-ai-card"
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
};

export default FeatureSection;
